import { useMemo, useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { closeLoading, openLoading } from "../../../../components/loadingModal/loading";
import { addGroupsToCourses, getCourse } from "../../../../actions/manageCoursesActions";
import { getAllGroups } from "../../../../actions/manageGroupsActions";
import addGroupsIcon from "../../../../assets/icons/btnIconGroups.png";
import alert from "../../../../components/AlertModal/alertModal";
import Button from "../../../../components/Button";
import Table from "../../../../components/Table";
import styles from "./assign_groups.module.css";

const AssignGroups = ({
    closeModal,
    groups,
    courseData,
    getAllGroups,
    addGroupsToCourses
}) => {
    const [groupsSelected, setGroupsSelected] = useState({});
    const [availableGroups, setAvailableGroups] = useState([]);
    const { courseId } = useParams();

    const currentGroups = courseData.name ? courseData.groups : [];

    // Memoized table columns
    const columns = useMemo(() => [
        {
            header: 'name',
            accessorKey: 'name',
            Header: () => <div className="table-title">GROUP NAME</div>,
            filterVariant: 'text',
            size: 300,
            cell: (info) => <div className={styles.groupNameCell}>{info.getValue()}</div>
        },
        {
            header: 'users',
            accessorKey: 'countUsers',
            Header: () => <div className="table-title">USERS</div>,
            enableColumnFilter: false,
            enableSorting: false,
            size: 100,
            cell: (info) => (
                <div className={styles.userCount}>
                    <span className={styles.userCountNumber}>{info.getValue() || 0}</span>
                    <span className={styles.userCountLabel}>users</span>
                </div>
            )
        },
    ], []);

    // Filter available groups
    useEffect(() => {
        if (currentGroups.length > 0) {
            const currentGroupIds = new Set(currentGroups.map(group => group.idGroup));
            const filteredGroups = groups.filter(group => !currentGroupIds.has(group.idGroup));
            setAvailableGroups(filteredGroups);
        } else {
            setAvailableGroups(groups);
        }
    }, [groups, currentGroups]);

    // Initialize groups
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                openLoading();
                await getAllGroups({ column: "name", order: "asc" });
            } catch (error) {
                alert("Error", error.message);
            } finally {
                closeLoading();
            }
        };

        fetchGroups();
    }, [courseId, getAllGroups]);

    // Event handlers
    const handleCancel = useCallback((e) => {
        e?.preventDefault();
        setGroupsSelected({});
        closeModal();
    }, [closeModal]);

    const handleSave = useCallback(async () => {
        if (Object.keys(groupsSelected).length === 0) return;

        openLoading();

        try {
            const selectedIds = Object.keys(groupsSelected);
            const groupsToAdd = groups.filter(group =>
                selectedIds.includes(`${group.idGroup}`)
            );

            await addGroupsToCourses({ groups: groupsToAdd });
            setGroupsSelected({});
            closeModal();
        } catch (error) {
            alert("Error", error.message);
        } finally {
            closeLoading();
        }
    }, [groupsSelected, groups, addGroupsToCourses, closeModal]);

    const hasSelectedGroups = Object.keys(groupsSelected).length > 0;
    const hasAvailableGroups = availableGroups.length > 0;
    const totalUsers = availableGroups.reduce((sum, group) => sum + (group.countUsers || 0), 0);

    return (
        <div className={styles.overlay}>
            <div className={styles.modalContainer}>
                <div className={styles.modalContent}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.titleSection}>
                            <h2 className={styles.title}>Add Groups to Course</h2>
                            <p className={styles.subtitle}>
                                Select groups to assign them to this course
                                {courseData.name && ` - ${courseData.name}`}
                            </p>
                        </div>
                        <button
                            className={styles.closeButton}
                            onClick={handleCancel}
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>

                    {/* Stats Bar */}
                    <div className={styles.statsBar}>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{availableGroups.length}</span>
                            <span className={styles.statLabel}>Available Groups</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{totalUsers}</span>
                            <span className={styles.statLabel}>Total Users</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{Object.keys(groupsSelected).length}</span>
                            <span className={styles.statLabel}>Selected</span>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className={styles.tableSection}>
                        {hasAvailableGroups ? (
                            <div className={styles.tableWrapper}>
                                <Table
                                    rowId="idGroup"
                                    data={availableGroups}
                                    columns={columns}
                                    height="400px"
                                    enableRowSelection
                                    setSelectedRows={setGroupsSelected}
                                />
                            </div>
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateIcon}>🏢</div>
                                <h3 className={styles.emptyStateTitle}>No Groups Available</h3>
                                <p className={styles.emptyStateText}>
                                    {currentGroups.length > 0
                                        ? "All groups are already assigned to this course."
                                        : "No groups available in the system."
                                    }
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className={styles.footer}>
                        <Button
                            onClick={handleCancel}
                            label="CANCEL"
                            size="medium"
                            styleButton="normal"
                        />
                        <Button
                            disabled={!hasSelectedGroups}
                            onClick={handleSave}
                            label={`ADD ${Object.keys(groupsSelected).length} GROUP${Object.keys(groupsSelected).length !== 1 ? 'S' : ''}`}
                            size="medium"
                            styleButton="light"
                            image={
                                <img
                                    src={addGroupsIcon}
                                    alt="Add Groups"
                                    className={styles.buttonIcon}
                                />
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Memoized dispatch functions
const mapDispatchToProps = {
    getAllGroups: (params) => getAllGroups(params),
    addGroupsToCourses: (params) => addGroupsToCourses(params),
    getCourse: (params) => getCourse(params)
};

// Memoized state mapping
const mapStateToProps = (state) => ({
    groups: state.manageGroups.allGroups,
    courseData: state.manageCourses.courseData,
});

export default connect(mapStateToProps, mapDispatchToProps)(AssignGroups);